import { useQuery } from "@apollo/client";
import _ from "lodash";
import { ReactElement, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  CampaignStatus,
  GetInboxQuery,
  GetInboxQueryVariables,
  TexterCampaignSendStatus,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import { GET_INBOX } from "~frontend/graphql/inboxes";

import { InboxArchived } from "./InboxArchived";
import { InboxAssignments } from "./InboxAssignments";
import { NonCampaignInbox } from "./NonCampaignInbox";

export function Inbox(): ReactElement {
  const { inboxId: maybeInboxId } = useParams<{
    inboxId: string;
  }>();
  const inboxId = maybeInboxId!;

  const getInboxResponse = useQuery<GetInboxQuery, GetInboxQueryVariables>(
    GET_INBOX,
    { variables: { input: { inboxId } } }
  );

  useEffect(() => {
    const hasPendingTexterSend =
      (getInboxResponse.data?.getInbox.__typename === "CampaignInboxSummary" &&
        getInboxResponse.data?.getInbox.campaign?.myCampaignSends?.some(
          send => send.status === TexterCampaignSendStatus.Pending
        )) ||
      (getInboxResponse.data?.getInbox.__typename === "GeneralInboxDetails" &&
        getInboxResponse.data?.getInbox.statsByCampaign.some(stats =>
          stats.campaign?.myCampaignSends.some(
            send => send.status === TexterCampaignSendStatus.Pending
          )
        ));

    if (hasPendingTexterSend) {
      getInboxResponse.startPolling(1000);
    } else {
      getInboxResponse.startPolling(60 * 1000);
    }

    return () => getInboxResponse.stopPolling();
  });

  if (getInboxResponse.loading) {
    return <Loading />;
  }

  if (getInboxResponse.error) {
    return <ErrorCard errors={[getInboxResponse.error]} />;
  }

  const inbox = getInboxResponse.data!.getInbox;

  switch (inbox.__typename) {
    case "CampaignInboxSummary":
      if (inbox.campaign.status === CampaignStatus.Archived) {
        return <InboxArchived />;
      }

      return (
        <InboxAssignments
          inbox={inbox}
          campaignId={inbox.campaign.id}
          refetchInbox={getInboxResponse.refetch}
        />
      );
    case "EscalationInboxDetails":
    case "GeneralInboxDetails":
      return <NonCampaignInbox inbox={inbox} />;
    default: {
      const _exhaustiveCheck: never = inbox;
      return _exhaustiveCheck;
    }
  }
}
